import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import Logo from "@/components/Logo";
import { NAV } from "@/constants/testIds";
import { Button } from "@/components/ui/button";

export default function Navbar({ settings }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isHome = pathname === "/";

  const scrollTo = (id) => (e) => {
    if (!isHome) return; // let Link handle navigation
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  const links = [
    { id: "services", label: "Services", testId: NAV.services },
    { id: "packages", label: "Packages", testId: NAV.packages },
    { id: "gallery", label: "Gallery", testId: NAV.gallery },
    { id: "contact", label: "Contact", testId: NAV.contact },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-7xl px-5 md:px-10 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.home}
          className="flex items-center"
          onClick={() => setOpen(false)}
        >
          <Logo logoUrl={settings?.logo_url} size="sm" />
        </Link>

        <div className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <a
              key={l.id}
              href={isHome ? `#${l.id}` : `/#${l.id}`}
              data-testid={l.testId}
              onClick={scrollTo(l.id)}
              className="text-sm uppercase tracking-widest text-zinc-300 hover:text-cyan-400 transition-colors"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-3">
          <a
            href={`tel:${(settings?.phone || "+16197692398").replace(/[^+\d]/g, "")}`}
            data-testid={NAV.phoneCta}
            className="hidden md:inline-flex items-center gap-2 text-zinc-200 hover:text-cyan-400 text-sm font-medium transition-colors"
          >
            <Phone className="w-4 h-4" />
            <span>{settings?.phone || "+1 (619) 769-2398"}</span>
          </a>
          <Button
            asChild
            className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-semibold uppercase tracking-widest text-xs px-5 h-11 shadow-[0_0_18px_rgba(0,229,255,0.35)]"
            data-testid={NAV.quoteCta}
          >
            <a href={isHome ? "#contact" : "/#contact"} onClick={scrollTo("contact")}>
              Get a Quote
            </a>
          </Button>
        </div>

        <button
          type="button"
          className="lg:hidden text-white p-2"
          onClick={() => setOpen((o) => !o)}
          data-testid={NAV.mobileToggle}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10">
          <div className="px-5 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.id}
                href={isHome ? `#${l.id}` : `/#${l.id}`}
                data-testid={`${l.testId}-mobile`}
                onClick={scrollTo(l.id)}
                className="text-lg uppercase tracking-widest text-zinc-200 hover:text-cyan-400"
              >
                {l.label}
              </a>
            ))}
            <a
              href={`tel:${(settings?.phone || "+16197692398").replace(/[^+\d]/g, "")}`}
              className="flex items-center gap-2 text-zinc-200"
            >
              <Phone className="w-4 h-4 text-cyan-400" />
              {settings?.phone || "+1 (619) 769-2398"}
            </a>
            <Button
              asChild
              className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-semibold uppercase tracking-widest text-xs h-11"
            >
              <a href={isHome ? "#contact" : "/#contact"} onClick={scrollTo("contact")}>
                Get a Quote
              </a>
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
